import React from 'react'
import { TertiaryButton, Box } from '@chilipiper/design-system'
import { useWorkspace } from '@chilipiper/workspace'
import { LocationType, PickedMeetingLocation } from '@chilipiper/api-type-def'
import { Workspaces } from '@chilipiper/models'
import { useLocale } from '@chilipiper/locale'
import {
  MeetingLocation,
  isPickedMeetingLocationValid,
  InviteAdditionalGuestsToggle,
  AdditionalGuestsTextField,
} from '@chilipiper/booking-flow-legacy'
import { GuestFormsCFA, GuestFormsCFAField, AvailabilityPayload } from './guest-forms-cfa'

interface Props extends Omit<AvailabilityPayload, 'locale'> {
  additionalGuestsOpen: boolean
  buttonLabel: string
  guests: string[]
  isRouterPage: boolean
  locations: LocationType[]
  onChange: (values: any) => void
  onConfirm: () => void
  onMeetingLocationChange: (selectedLocation: PickedMeetingLocation) => void
  pickedMeetingLocation: PickedMeetingLocation
  setAdditionalGuestsOpen: (isOpen: boolean) => void
  updateGuests: (guests: string[]) => void
}

export const GuestForms = ({
  leadFields,
  contactFields,
  prospect,
  onChange,
  onMeetingLocationChange,
  buttonLabel,
  onConfirm,
  form,
  additionalGuestsOpen,
  setAdditionalGuestsOpen,
  guests,
  updateGuests,
  locations = [],
  isHubspot,
  pickedMeetingLocation,
  isRouterPage,
}: Props) => {
  const locale = useLocale()
  const workspace = useWorkspace()
  const isPickedMeetingLocationInvalid = !isPickedMeetingLocationValid(pickedMeetingLocation)

  return (
    <GuestFormsCFA
      leadFields={leadFields}
      contactFields={contactFields}
      form={form}
      isHotWorkspace={Workspaces.isHot(workspace)}
      locale={locale}
      isHubspot={isHubspot}
      onChange={onChange}
      prospect={prospect}
    >
      {(fields, invalid) => {
        return (
          <Box w='100%' maxWidth={23} mb={6}>
            {fields.map((field, index) => {
              const isHiddenField = field.hidden || !field.name ? 'none' : undefined
              return (
                <Box my={5} key={`${field.name}_${index}`} display={isHiddenField}>
                  <GuestFormsCFAField
                    required={field.required}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    customSelector='field-selected'
                    options={field.options}
                    autoFocus={index === 0}
                  />
                </Box>
              )
            })}
            {locations.length > 1 && (
              <Box mt={6}>
                <MeetingLocation
                  pickedMeetingLocation={pickedMeetingLocation}
                  locations={locations}
                  onSelect={onMeetingLocationChange}
                />
              </Box>
            )}
            {!isRouterPage && (
              <Box mt={6}>
                <InviteAdditionalGuestsToggle
                  key='invite-additional-guests-toggle'
                  isOpen={additionalGuestsOpen}
                  setOpen={setAdditionalGuestsOpen}
                />
              </Box>
            )}
            {additionalGuestsOpen ? (
              <AdditionalGuestsTextField
                key='additional-guests-text-field'
                guests={guests}
                updateGuests={updateGuests}
              />
            ) : null}
            <TertiaryButton
              data-chili-css='confirm-button'
              data-test-id='ProspectForm-book'
              mt={7}
              w='100%'
              disabled={invalid || isPickedMeetingLocationInvalid}
              onClick={onConfirm}
            >
              {buttonLabel}
            </TertiaryButton>
          </Box>
        )
      }}
    </GuestFormsCFA>
  )
}
